import React, { useMemo } from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Card, Descriptions, Form, Input, InputNumber, Switch, Select, Tag, Row, Col, TimePicker, Tooltip, Space } from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * 欄位區塊組件 - 根據配置自動渲染顯示/編輯模式
 *
 * @param {Object} config - 區塊配置 { title, fields: [{ key, label, type, options?, span? }] }
 * @param {Object} data - 資料物件
 * @param {Object} form - Ant Design Form 實例（編輯模式需要）
 * @param {Boolean} editMode - 是否為編輯模式
 * @param {String} i18nPrefix - i18n 前綴，預設 'operator.merchants.show'
 * @param {Number} column - 每行欄位數，預設 2
 */
const FieldSection = ({
  config,
  data,
  form,
  editMode = false,
  i18nPrefix = 'operator.merchants.show',
  column = 2,
  disabled = false,
  extra,
}) => {
  const { t } = useTranslation();

  // 收集 showIf / requiredIf 內被參照的欄位名稱，僅 watch 這些欄位以降低重渲染負擔
  const watchKeys = useMemo(() => {
    const keys = new Set();
    config.fields.forEach((f) => {
      [ f.showIf, f.requiredIf ].forEach((cond) => {
        if (cond) Object.keys(cond).forEach((k) => keys.add(k));
      });
    });
    return Array.from(keys);
  }, [ config ]);

  // 編輯模式 watch 條件依賴欄位；顯示模式不訂閱（Form.useWatch 第二參數傳 undefined 即為 no-op）
  const watchedValues = Form.useWatch(
    (values) => watchKeys.reduce((acc, k) => {
      acc[k] = values?.[k];
      return acc;
    }, {}),
    editMode ? form : undefined
  );

  const matchesCondition = (condition) => {
    if (!condition) return true;
    const source = editMode ? watchedValues : data;
    if (!source) return false;
    return Object.entries(condition).every(([k, v]) => source[k] === v);
  };
  const shouldShow = (field) => matchesCondition(field.showIf);
  const isRequired = (field) => {
    if (field.required) return true;
    if (!field.requiredIf) return false;
    return matchesCondition(field.requiredIf);
  };

  const getLabel = (labelKey) => {
    return t(`${i18nPrefix}.${labelKey}`);
  };

  // 渲染帶 tooltip 的欄位標籤
  const getFieldLabel = (field) => {
    const label = getLabel(field.label);
    if (!field.tooltip) return label;
    return (
      <Space>
        {label}
        <Tooltip title={getLabel(field.tooltip)} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 400 }}>
          <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
        </Tooltip>
      </Space>
    );
  };

  // 渲染 section title（支援 config.tooltip）
  const renderTitle = () => {
    const title = getLabel(config.title);
    if (!config.tooltip) return title;
    return (
      <Space>
        {title}
        <Tooltip title={getLabel(config.tooltip)} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 480 }}>
          <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
        </Tooltip>
      </Space>
    );
  };

  // 渲染顯示模式的值
  const renderDisplayValue = (field) => {
    const value = data[field.key];

    switch (field.type) {
      case 'currency':
        return value != null ? `$${value}` : '-';

      case 'boolean':
        return value ? (
          <Tag color="success">{t('common.yes')}</Tag>
        ) : (
          <Tag>{t('common.no')}</Tag>
        );

      case 'select': {
        if (!value) return '-';
        const optionLabelKey = `${i18nPrefix}.${field.optionsPrefix || field.key + 's'}.${value}`;
        const label = t(optionLabelKey, { defaultValue: value });
        if (field.appendCustomFrom && value === 'custom') {
          const keys = Array.isArray(field.appendCustomFrom) ? field.appendCustomFrom : [field.appendCustomFrom];
          const parts = keys.map((k) => data[k]).filter(Boolean);
          const customValue = parts.length > 0 ? parts.join(' ') : '-';
          return `${label}（${customValue}）`;
        }
        return label;
      }

      case 'multiselect':
        if (!value || !Array.isArray(value) || value.length === 0) return '-';
        return value.map((v) => {
          const label = t(`${i18nPrefix}.${field.optionsPrefix || field.key + 's'}.${v}`, { defaultValue: String(v) });
          return <Tag key={v} style={{ marginBottom: 2 }}>{label}</Tag>;
        });

      case 'time':
        return value || '-';

      case 'number':
        return value != null ? value : '-';

      case 'custom':
        // display mode 沿用 value 顯示；custom 主要為 edit-mode 客製 input 用
        return value || '-';

      case 'text':
      default:
        return value || '-';
    }
  };

  // 渲染編輯模式的表單項
  const renderFormItem = (field) => {
    const colSpan = field.span || (24 / column);

    const colOffset = field.offset || 0;

    let inputComponent;
    switch (field.type) {
      case 'currency':
        inputComponent = (
          <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="$" disabled={disabled} />
        );
        break;

      case 'boolean':
        return (
          <Col span={colSpan} offset={colOffset} key={field.key}>
            <Form.Item
              name={field.key}
              label={getFieldLabel(field)}
              valuePropName="checked"
            >
              <Switch disabled={disabled} />
            </Form.Item>
          </Col>
        );

      case 'select':
        inputComponent = (
          <Select allowClear={!field.required} disabled={disabled}>
            {(field.options || []).map((opt) => (
              <Select.Option key={opt} value={opt}>
                {t(`${i18nPrefix}.${field.optionsPrefix || field.key + 's'}.${opt}`)}
              </Select.Option>
            ))}
          </Select>
        );
        break;

      case 'multiselect':
        inputComponent = (
          <Select mode="multiple" allowClear disabled={disabled}>
            {(field.options || []).map((opt) => (
              <Select.Option key={opt.value ?? opt} value={opt.value ?? opt}>
                {t(`${i18nPrefix}.${field.optionsPrefix || field.key + 's'}.${opt.value ?? opt}`, { defaultValue: opt.label ?? String(opt) })}
              </Select.Option>
            ))}
          </Select>
        );
        break;

      case 'time':
        return (
          <Col span={colSpan} offset={colOffset} key={field.key}>
            <Form.Item
              name={field.key}
              label={getFieldLabel(field)}
              getValueFromEvent={(time) => time ? time.format('HH:mm') : null}
              getValueProps={(value) => ({ value: value ? dayjs(value, 'HH:mm') : null })}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} minuteStep={15} disabled={disabled} />
            </Form.Item>
          </Col>
        );

      case 'number':
        inputComponent = (
          <InputNumber min={field.min || 0} style={{ width: '100%' }} disabled={disabled} />
        );
        break;

      case 'textarea':
        inputComponent = <Input.TextArea rows={field.rows || 2} disabled={disabled} />;
        break;

      case 'custom':
        // 自訂渲染：field.renderInput({ form, disabled, field }) 回傳 Input 元件
        // 用於需要監聽 form state、跨欄位連動等情境（例如 TWZIP city/district 自動填 postal_code）
        if (typeof field.renderInput === 'function') {
          inputComponent = field.renderInput({ form, disabled, field });
        } else {
          inputComponent = <Input placeholder={field.placeholder} disabled={disabled} />;
        }
        break;

      case 'text':
      default:
        inputComponent = <Input placeholder={field.placeholder} disabled={disabled} />;
        break;
    }

    return (
      <Col span={colSpan} offset={colOffset} key={field.key}>
        <Form.Item
          name={field.key}
          label={getFieldLabel(field)}
          rules={isRequired(field) ? [{ required: true, message: t('common.validation.required') }] : undefined}
        >
          {inputComponent}
        </Form.Item>
      </Col>
    );
  };

  const cardHeadStyle = { backgroundColor: '#fafafa', fontWeight: 'normal' };

  // 顯示模式：showIf=false 或 hideInDisplay 直接過濾
  if (!editMode) {
    const visibleFields = config.fields.filter((f) => !f.hideInDisplay && shouldShow(f));
    return (
      <Card size="small" title={renderTitle()} extra={extra} style={{ height: '100%' }} headStyle={cardHeadStyle}>
        <Descriptions column={column} size="small">
          {visibleFields.map((field) => (
            <Descriptions.Item
              key={field.key}
              label={getLabel(field.label)}
              span={field.displaySpan || 1}
            >
              {renderDisplayValue(field)}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Card>
    );
  }

  // 編輯模式：showIf=false 時保留 Col 佔位，避免後續欄位跳位
  return (
    <Card size="small" title={renderTitle()} extra={extra} style={{ height: '100%', marginBottom: 16 }} headStyle={cardHeadStyle}>
      <Row gutter={24}>
        {config.fields.map((field) => {
          if (shouldShow(field)) return renderFormItem(field);
          if (field.hidePlaceholder) return null;
          return <Col span={field.span || (24 / column)} key={field.key} />;
        })}
      </Row>
    </Card>
  );
};

export default FieldSection;
